import { useState, useRef, useEffect } from 'react'
import { saveNewReport, getReportList, updateReports, deleteReports, selectReportBuilderOptions, ReportStatusIndicatorEnum } from '../../../api/reports'
import { GridBlocksEnum } from './blocks'
import { useDebouncedCallback } from 'use-debounce'
import { toast } from 'react-toastify'

export { ReportStatusIndicatorEnum }

// helper
// update reports with the latest data from the db
const refreshReportData = async (reportList) => {
  if (!reportList) return

  // Process all reports
  const updatedReports = await Promise.all(reportList.map(processReport))

  return updatedReports // Explicitly return the updated reports
}

const processReport = async (report) => {
  if (!report || !report.content) return report

  const updatedContent = await Promise.all(report.content.map(processContentItem))

  return { ...report, content: updatedContent }
}

// update charts and theme comments with the latest data
const processContentItem = async (item) => {
  if (item.type !== GridBlocksEnum.CUSTOM_CHART && item.type !== GridBlocksEnum.COMMENTS) {
    return item
  }

  const resData = await selectReportBuilderOptions(item.type, item.config)

  return { ...item, content: resData }
}

const useManageReports = (items, setItems, initialTitle, initialId, initialState = ReportStatusIndicatorEnum.UNSAVED) => {
  const [activeReportId, setActiveReportId] = useState(initialId)
  const [reportTitleInput, setReportTitleInput] = useState(initialTitle)
  const [reportsData, setReportData] = useState([])
  const [reportStatusIndicator, setReportStatusIndicator] = useState(initialState)
  const reportNameRef = useRef(null)

  // fetch reports list on first render
  useEffect(() => {
    handleFetchReportList()
  }, [])

  const getCurrentReport = () => reportsData?.find((report) => report?.id === activeReportId)

  const updateReportsInBackground = useDebouncedCallback(async () => {
    try {
      if (!activeReportId) {
        return
      }
      const currentReport = getCurrentReport()
      if (currentReport?.createdAt) {
        const UpdatedReportData = {
          id: currentReport?.id,
          name: reportTitleInput,
          content: items
        }
        setReportStatusIndicator(ReportStatusIndicatorEnum.SAVING)

        await updateReports(UpdatedReportData, setReportStatusIndicator)
        await handleFetchReportList()
        setReportStatusIndicator(ReportStatusIndicatorEnum.SAVED)
      }
    } catch (e) {
      toast.error(`could not update report`)
      console.error(e)
      setReportStatusIndicator(ReportStatusIndicatorEnum.ERROR)
    }
  }, 2000)

  const hasMountedRef = useRef(false)

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      return
    }
    setReportStatusIndicator(ReportStatusIndicatorEnum.UNSAVED)
    updateReportsInBackground()

    return () => {
      if (typeof updateReportsInBackground.cancel === 'function') {
        updateReportsInBackground.cancel()
      }
    }
  }, [updateReportsInBackground, items, reportTitleInput])

  const handleCreateNewReport = (content) => {
    try {
      // focus input
      if (reportNameRef.current) {
        reportNameRef.current.focus()
      }
      setActiveReportId(null)
      setReportTitleInput('')
      setReportStatusIndicator(ReportStatusIndicatorEnum.UNSAVED)
    } catch (e) {
      console.error(e)
    }
  }

  const handleUpdateReportName = (event) => {
    setReportStatusIndicator(ReportStatusIndicatorEnum.UNSAVED)
    setReportTitleInput(event.target.value)
  }

  const handleFetchReportList = async () => {
    try {
      const reportList = await getReportList()
      const updatedReportList = await refreshReportData(reportList)
      setReportData(updatedReportList)
    } catch (e) {
      toast.error('could not fetch reports')
      console.error(e)
    }
  }

  const handleSaveNewReport = async () => {
    try {
      if (!reportTitleInput || items.length === 0) {
        setReportStatusIndicator(ReportStatusIndicatorEnum.VALIDATION_ERROR)
        return
      }
      const currentReport = getCurrentReport()
      const reportAlreadyExists = !!currentReport?.createdAt

      if (reportAlreadyExists) {
        const updatedReportData = {
          id: currentReport?.id,
          name: reportTitleInput,
          content: items
        }
        await updateReports(updatedReportData, setReportStatusIndicator)
        await handleFetchReportList()
        setReportStatusIndicator(ReportStatusIndicatorEnum.SAVED)
        return
      }

      const newReportTemplate = {
        name: reportTitleInput,
        content: items
      }

      const newReportData = await saveNewReport(newReportTemplate, setReportStatusIndicator)
      if (!newReportData) {
        throw new Error('could not save report')
      }

      await handleFetchReportList()
      setActiveReportId(newReportData?.id)
      setReportStatusIndicator(ReportStatusIndicatorEnum.SAVED)
    } catch (e) {
      toast.error(`could not save report ${reportTitleInput}`)
      console.error(e)
    }
  }

  const handleSwitchReports = (targetId) => {
    const selectedReport = reportsData?.find((report) => report?.id === targetId)
    setActiveReportId(selectedReport?.id)
    setReportTitleInput(selectedReport?.name)
    setReportStatusIndicator(ReportStatusIndicatorEnum.SAVED)
    setItems(selectedReport?.content)
  }

  const handleDeleteReports = async (targetId, reportName) => {
    try {
      if (!targetId) {
        throw new Error('could not delete report')
      }
      await deleteReports(targetId)
      await handleFetchReportList()
      toast.success(`Report with the name "${reportName}" has been deleted`)
      setReportTitleInput('')
      setReportStatusIndicator(ReportStatusIndicatorEnum.UNSAVED)
      setActiveReportId(null)
    } catch (e) {
      toast.error('Error, your report could not be deleted')
    }
  }

  return {
    reportStatusIndicator,
    reportsData,
    handleUpdateReportName,
    reportTitleInput,
    handleCreateNewReport,
    reportNameRef,
    handleSaveNewReport,
    handleFetchReportList,
    getCurrentReport,
    handleSwitchReports,
    activeReportId,
    handleDeleteReports
  }
}

export default useManageReports
export { useManageReports }
